import { memo, useCallback, useState } from 'react'
import { FileSpreadsheet } from 'lucide-react'
import { announcementsApi, type MemberImportPreview, type MemberImportResult } from '../../lib/infoserviceApi.js'
import { frenchDate } from '../../lib/memberQuota.js'
import { ModalShell } from '../common/ModalShell.js'
import { Spinner } from '../common/Spinner.js'

/**
 * Import du fichier du club (« FICHIER CLUB EN COURS.ods ») : aperçu, puis
 * import. Ajoute et met à jour les adhérents de l'onglet « EN COURS », ne
 * supprime rien, et ne touche ni les annonces, ni leurs jours, ni l'antenne.
 */
export const MemberImportModal = memo(function MemberImportModal({ channelId, onClose, onImported }: {
  channelId: string
  onClose: () => void
  onImported: (result: MemberImportResult) => void
}) {
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<MemberImportPreview | null>(null)
  const [busy, setBusy] = useState<'preview' | 'apply' | null>(null)
  const [error, setError] = useState('')

  const pick = useCallback(async (picked: File | null) => {
    setFile(picked)
    setPreview(null)
    setError('')
    if (!picked) return
    setBusy('preview')
    try {
      setPreview(await announcementsApi.members.previewImport(channelId, picked))
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Lecture du fichier impossible')
    } finally {
      setBusy(null)
    }
  }, [channelId])

  const apply = useCallback(async () => {
    if (!file) return
    setBusy('apply')
    setError('')
    try {
      onImported(await announcementsApi.members.applyImport(channelId, file))
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Import impossible')
      setBusy(null)
    }
  }, [channelId, file, onImported])

  return (
    <ModalShell title="Importer le fichier du club" onClose={onClose} closeDisabled={busy === 'apply'} size="wide" className="spellings-modal">
      <div className="tp-modal__body member-import">
        <p className="announcement-drawer__hint">
          Le classeur du club (.ods), onglet « EN COURS ». Les adhérents sont ajoutés ou mis à jour ; rien n’est supprimé.
          Le droit annuel vient de la catégorie (6 semaines pour un particulier, 52 pour une entreprise, 12 sinon) :
          le « crédit » du fichier, tenu à la main, n’est pas repris. Les annonces et le calendrier ne changent pas.
        </p>
        <label className="tp-form-field">
          <span className="tp-field-label">Fichier</span>
          <input className="tp-input" type="file" accept=".ods,application/vnd.oasis.opendocument.spreadsheet" disabled={busy !== null}
            onChange={event => void pick(event.target.files?.[0] ?? null)} />
        </label>

        {busy === 'preview' && <div className="tp-empty-state" aria-live="polite"><Spinner /> Lecture du fichier…</div>}
        {error && <div className="tp-feedback tp-feedback--error" role="alert">{error}</div>}

        {preview && (
          <>
            <div className="member-import__summary" aria-live="polite">
              <div className="announcements-summary__metric">
                <span className="announcements-summary__label">Adhérents lus</span>
                <span className="announcements-summary__value">{preview.rows.length}</span>
                <span className="announcements-summary__label">{preview.created} nouveaux · {preview.updated} mis à jour</span>
              </div>
              <div className="announcements-summary__metric">
                <span className="announcements-summary__label">Rattachées d’office</span>
                <span className="announcements-summary__value">{preview.attachedSpellings}</span>
                <span className="announcements-summary__label">écritures de la feuille · {preview.attachedAnnouncements} annonces</span>
              </div>
              <div className="announcements-summary__metric">
                <span className="announcements-summary__label">À rattacher ensuite</span>
                <span className="announcements-summary__value">{preview.unresolvedSpellings}</span>
                <span className="announcements-summary__label">écritures · {preview.unresolvedAnnouncements} annonces</span>
              </div>
            </div>
            {preview.warnings.length > 0 && (
              <div className="tp-feedback tp-feedback--warning" role="status">
                {preview.warnings.length} réserve{preview.warnings.length > 1 ? 's' : ''} : {preview.warnings.join(' ')}
              </div>
            )}
            <div className="member-import__scroll">
              <table className="members-table">
                <thead>
                  <tr>
                    <th scope="col">Adhérent</th>
                    <th scope="col">Catégorie</th>
                    <th scope="col">Adhésion</th>
                    <th scope="col" className="members-table__number">Droit annuel</th>
                    <th scope="col">Dans TrafficPlay</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map(row => (
                    <tr key={row.row} className="members-table__row">
                      <th scope="row" className="member-import__name">{row.name}</th>
                      <td className="members-table__date">{row.category ? `C${row.category}` : '—'}</td>
                      <td className="members-table__date">{row.membershipDate ? frenchDate(row.membershipDate) : <span className="members-table__missing">à renseigner</span>}</td>
                      <td className="members-table__number">{row.weekQuota}</td>
                      <td>{row.existingName ? <span className="member-import__state">Mis à jour</span> : <span className="member-import__state is-new">Nouveau</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <div className="tp-modal-actions">
          <button type="button" className="tp-button" onClick={onClose} disabled={busy === 'apply'}>Annuler</button>
          <button type="button" className="tp-button tp-button--primary" disabled={!preview || busy !== null} onClick={() => void apply()}>
            {busy === 'apply' ? <Spinner /> : <FileSpreadsheet size={14} aria-hidden="true" />}
            {preview ? ` Importer ${preview.rows.length} adhérents` : ' Importer'}
          </button>
        </div>
      </div>
    </ModalShell>
  )
})
